import type {
  ReceivingBarcodeTeachInput,
  ReceivingBarcodeTeachResult,
  CageCloseInput,
  CageCloseResult,
  CageScanInput,
  CageScanResult,
  CageSummary,
  ListResponse,
  PurchaseOrderCreateInput,
  PurchaseOrderDetail,
  PurchaseOrderListItem,
  PutawayInput,
  PutawayQueueItem,
  PutawayResult,
  ReceiptCloseInput,
  ReceiptCloseResult,
  ReceiptDetail,
  ReceiptLineAddInput,
  ReceiptLineAddResult,
  ReceiptListItem,
  ReceiptOpenInput,
  ReceiptPalletizeInput,
  ReceiptPalletizeResult,
  ReceiptProduceReceiveInput,
  ReceiptProduceReceiveResult,
  ReceivingScanInput,
  ReceivingScanResult,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryKey, listQueryParams, type ListState } from '../../lib/list-query';

/**
 * The receiving module's HTTP surface, in one file.
 *
 * Every mutation sends a client-generated `commandId`: the server dedupes on it
 * (`command_dedupe`), so a double-tapped "Ta emot" on a tablet at the dock
 * receives once, not twice. That is the same guarantee the offline scanner
 * relies on — the web just gets it for free by sending the field.
 */

export function newCommandId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function withCommandId<T extends object>(body: T): T & { commandId: string } {
  return { ...body, commandId: newCommandId() };
}

// ---------------------------------------------------------------------------
// query keys
// ---------------------------------------------------------------------------

export const receivingKeys = {
  purchaseOrders: (state: ListState) => ['purchase-orders', listQueryKey(state)] as const,
  purchaseOrder: (id: string) => ['purchase-orders', 'detail', id] as const,
  receipts: (state: ListState) => ['receipts', listQueryKey(state)] as const,
  receipt: (id: string) => ['receipts', 'detail', id] as const,
  putawayQueue: () => ['receiving', 'putaway-queue'] as const,
  cage: (cageCode: string) => ['receiving', 'cage', cageCode] as const,
} as const;

// ---------------------------------------------------------------------------
// purchase orders
// ---------------------------------------------------------------------------

export const purchaseOrdersApi = {
  list: (state: ListState) =>
    api.get<ListResponse<PurchaseOrderListItem>>('/purchase-orders', {
      query: listQueryParams(state),
    }),
  detail: (id: string) => api.get<PurchaseOrderDetail>(`/purchase-orders/${id}`),
  create: (body: PurchaseOrderCreateInput) =>
    api.post<{ poId: string; poNo: string }>('/purchase-orders', withCommandId(body)),
  cancel: (id: string, reason?: string) =>
    api.post<PurchaseOrderDetail>(`/purchase-orders/${id}/cancel`, withCommandId({ reason })),
};

// ---------------------------------------------------------------------------
// receipts
// ---------------------------------------------------------------------------

export const receiptsApi = {
  list: (state: ListState) =>
    api.get<ListResponse<ReceiptListItem>>('/receipts', { query: listQueryParams(state) }),
  detail: (id: string) => api.get<ReceiptDetail>(`/receipts/${id}`),
  open: (body: ReceiptOpenInput) => api.post<ReceiptDetail>('/receipts', withCommandId(body)),
  addLine: (receiptId: string, body: Omit<ReceiptLineAddInput, 'receiptId'>) =>
    api.post<ReceiptLineAddResult>(`/receipts/${receiptId}/lines`, withCommandId(body)),
  voidLine: (receiptId: string, lineId: string, reason?: string) =>
    api.post<ReceiptDetail>(
      `/receipts/${receiptId}/lines/${lineId}/void`,
      withCommandId({ reason }),
    ),
  palletize: (receiptId: string, body: Omit<ReceiptPalletizeInput, 'receiptId'>) =>
    api.post<ReceiptPalletizeResult>(`/receipts/${receiptId}/palletize`, withCommandId(body)),
  produce: (receiptId: string, body: Omit<ReceiptProduceReceiveInput, 'receiptId'>) =>
    api.post<ReceiptProduceReceiveResult>(`/receipts/${receiptId}/produce`, withCommandId(body)),
  close: (receiptId: string, body: Omit<ReceiptCloseInput, 'receiptId'>) =>
    api.post<ReceiptCloseResult>(`/receipts/${receiptId}/close`, withCommandId(body)),
};

// ---------------------------------------------------------------------------
// scanning, put-away, cages
// ---------------------------------------------------------------------------

export const receivingApi = {
  scan: (body: ReceivingScanInput) => api.post<ReceivingScanResult>('/receiving/scan', body),
  teach: (body: ReceivingBarcodeTeachInput) =>
    api.post<ReceivingBarcodeTeachResult>('/receiving/teach', withCommandId(body)),
  putawayQueue: () => api.get<PutawayQueueItem[]>('/receiving/putaway/queue'),
  putaway: (body: PutawayInput) => api.post<PutawayResult>('/receiving/putaway', withCommandId(body)),
  cage: (cageCode: string) => api.get<CageSummary>(`/receiving/cages/${encodeURIComponent(cageCode)}`),
  cageScan: (body: CageScanInput) =>
    api.post<CageScanResult>('/receiving/cages/scan', withCommandId(body)),
  cageClose: (body: CageCloseInput) =>
    api.post<CageCloseResult>('/receiving/cages/close', withCommandId(body)),
};
